"use client";

import axios from "axios";
import { useState } from "react";

type NewsletterProps = {
  listID?: string;
  tagID?: string;
};

const toIds = (value?: string) =>
  (value || "")
    .replaceAll(" ", "")
    .split(",")
    .filter(Boolean)
    .map(Number);

/** Newsletter band — design `.nl`. Posts to the same endpoint as the shared Subscribe box. */
const Newsletter = ({ listID, tagID }: NewsletterProps) => {
  const [state, setState] = useState<"idle" | "sending" | "done">("idle");
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    setState("sending");

    const email = String(new FormData(e.currentTarget).get("email") || "").trim();

    try {
      const { data } = await axios.post("/api/subscribe/create", {
        first_name: "",
        email,
        lists: toIds(listID),
        tags: toIds(tagID),
        status: "yes",
        custom_values: { site_source: process.env.NEXT_PUBLIC_NEXTJS_SITE_URL },
      });

      if (data?.success) {
        setState("done");
      } else {
        setState("idle");
        setError(data?.message || "We could not subscribe you. Please try again.");
      }
    } catch {
      setState("idle");
      setError("We could not reach the server. Please try again in a moment.");
    }
  };

  return (
    <div className="jlt-relative jlt-overflow-hidden jlt-rounded-[30px] jlt-bg-[linear-gradient(120deg,#CBE4FF,#E4D9FF_55%,#FFD9C0)] jlt-p-[clamp(38px,5vw,58px)] jlt-text-center">
      {state === "done" ? (
        <div className="jlt-font-display jlt-text-[18px] jlt-font-extrabold jlt-text-pl-ink">
          🎉 You are in — welcome aboard!
        </div>
      ) : (
        <>
          <h2 className="jlt-relative jlt-mb-[10px] jlt-mt-0 jlt-font-display jlt-text-[clamp(24px,3.4vw,38px)] jlt-font-extrabold jlt-text-pl-ink">
            WordPress tips, twice a month
          </h2>
          <p className="jlt-relative jlt-mx-auto jlt-mb-[26px] jlt-max-w-[440px] jlt-leading-[1.62] jlt-text-pl-ink2">
            Join 12,000+ builders getting our best tutorials and product updates. No spam, ever.
          </p>

          <form
            onSubmit={handleSubmit}
            className="jlt-relative jlt-flex jlt-flex-wrap jlt-justify-center jlt-gap-[10px]"
          >
            <input
              type="email"
              name="email"
              required
              placeholder="you@example.com"
              className="jlt-min-w-[min(320px,70vw)] jlt-rounded-full jlt-border-[1.5px] jlt-border-transparent jlt-px-5 jlt-py-[14px] jlt-text-[15px] jlt-text-pl-ink jlt-shadow-pl-sm focus:jlt-border-pl-blue focus:jlt-outline-none"
            />
            <button
              type="submit"
              disabled={state === "sending"}
              className="jlt-inline-flex jlt-items-center jlt-justify-center jlt-rounded-full jlt-bg-pl-ink jlt-px-[30px] jlt-py-4 jlt-text-[16px] jlt-font-semibold jlt-text-white jlt-shadow-[0_10px_24px_-10px_rgba(22,19,14,.5)] jlt-transition-transform jlt-duration-200 jlt-ease-spring hover:-jlt-translate-y-[2px] disabled:jlt-opacity-60"
            >
              {state === "sending" ? "Subscribing…" : "Subscribe"}
            </button>
          </form>

          {error ? (
            <p className="jlt-relative jlt-mt-4 jlt-text-[14px] jlt-leading-[1.62] jlt-text-[#D14E12]">
              {error}
            </p>
          ) : null}
        </>
      )}
    </div>
  );
};

export default Newsletter;
